import { BookOpen, BookOpenCheck, CircleAlert } from 'lucide-react-native';
import type { ComponentType } from 'react';

import type { CitationRequest, CitationRequestStatus } from '../../model/citationRequest';

export type PillVariant = {
  verb: string;
  badge: string | null;
  /** The leading icon. Rendered as a `ButtonIcon` unless
   * `useSpinner` is set, in which case the spinner replaces it. */
  icon: ComponentType<{ className?: string }>;
  accessibilityLabel: string;
  /** When true, the pill is rendered but not interactive — the
   * workflow is mid-flight and there's nothing for the expert to do
   * here yet. */
  isDisabled?: boolean;
  /** When true, render an animated activity-indicator spinner in
   * place of the static icon. Used for the searching state so the
   * pill communicates "actively working" rather than a frozen
   * magnifying-glass glyph. */
  useSpinner?: boolean;
  /** When true, the badge uses an error-toned background instead of
   * the brand-primary background. */
  isError?: boolean;
  /** True only for the resting state (no active request) — the one
   * state where the expert can freely choose either reference flow. The
   * bubble renders the grouped two-button card in this case instead of
   * the single pill. */
  isResting?: boolean;
};

const STATEMENT_WORD = (count: number) => (count === 1 ? 'statement' : 'statements');

interface PickPillVariantInput {
  statementCount: number;
  citationRequest: CitationRequest | undefined;
}

/**
 * Pure variant resolver. Kept exported for unit testing so the matrix
 * of (status × counts) can be asserted without rendering.
 *
 * `Add references` stays the resting affordance even after refs are
 * attached: the product principle is that Primum does the searching,
 * so the expert should always be one tap away from triggering another
 * search rather than being nudged into manual edits. The statement
 * count is informational only — it describes message shape, never a
 * remaining-work quota.
 */
export function pickPillVariant({
  statementCount,
  citationRequest,
}: PickPillVariantInput): PillVariant {
  if (citationRequest) {
    return pickWorkflowVariant(citationRequest.status, citationRequest.proposedReferences.length);
  }

  return {
    verb: 'Add references',
    badge: `${statementCount} ${STATEMENT_WORD(statementCount)}`,
    icon: BookOpen,
    accessibilityLabel: `Add references, ${statementCount} ${STATEMENT_WORD(statementCount)}`,
    isResting: true,
  };
}

function pickWorkflowVariant(
  status: CitationRequestStatus,
  proposedReferenceCount: number
): PillVariant {
  switch (status) {
    case 'queued':
    case 'searching': {
      return {
        verb: 'Searching for references…',
        badge: null,
        // `BookOpen` is a defensive fallback; `useSpinner` overrides
        // it at render time so the user sees a live ActivityIndicator
        // instead of a static glyph while the workflow is running.
        icon: BookOpen,
        accessibilityLabel: 'Searching for references',
        isDisabled: true,
        useSpinner: true,
      };
    }
    case 'awaiting_review':
    case 'published': {
      // `published` mirrors `awaiting_review` here on purpose. Publish
      // is a non-destructive sync — the expert keeps the same approval
      // set and can keep republishing — so the pill should keep
      // advertising the review affordance instead of snapping back to
      // the resting "Find references" state.
      const label =
        proposedReferenceCount === 1
          ? '1 reference ready'
          : `${proposedReferenceCount} references ready`;

      return {
        verb: 'References ready',
        badge: 'Review',
        icon: BookOpenCheck,
        accessibilityLabel: `${label}, tap to review`,
      };
    }
    case 'failed': {
      return {
        verb: "Couldn't add references",
        badge: 'Retry',
        icon: CircleAlert,
        accessibilityLabel: "Couldn't add references, tap to retry",
        isError: true,
      };
    }
    case 'cancelled':
    default: {
      return {
        verb: 'Add references',
        badge: null,
        icon: BookOpen,
        accessibilityLabel: 'Add references',
      };
    }
  }
}
